import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, FESTIVAL_META, CATEGORY_LABELS, resolveMediaUrl } from "@/lib/api";
import ArtistSearch from "@/components/ArtistSearch";
import ArtistChip from "@/components/ArtistChip";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, ArrowLeft, Lock } from "lucide-react";
import { useState, useEffect } from "react";

const CAT_ORDER = ["headliner", "international", "danish"];

const CAT_META = {
  headliner: { icon: "★", points: 12, filter: "any", key: "headliners" },
  international: { icon: "🌍", points: 4, filter: "international", key: "international" },
  danish: { icon: "🇩🇰", points: 1, filter: "danish", key: "danish" },
};

export default function FestivalPrediction() {
  const { id } = useParams();
  const qc = useQueryClient();
  const meta = FESTIVAL_META[id];
  const { data: festData } = useQuery({ queryKey: ["festivals"], queryFn: () => api.get("/festivals").then(r => r.data) });
  const festival = festData?.festivals?.find(f => f.id === id);
  const { data: myPreds } = useQuery({ queryKey: ["my-predictions"], queryFn: () => api.get("/predictions/mine").then(r => r.data) });
  const { data: allArtists } = useQuery({ queryKey: ["all-artists"], queryFn: () => api.get("/artists", { params: { limit: 5000 } }).then(r => r.data) });

  const artistMap = Object.fromEntries((allArtists || []).map(a => [a.artist_id, a]));
  const locked = festData?.locked;

  const [local, setLocal] = useState({ headliners: [], international: [], danish: [], orange_opener: null });
  // Optimistic cache of newly-added artists so chips render immediately with image/name
  const [freshArtists, setFreshArtists] = useState({});
  const chipArtistMap = { ...artistMap, ...freshArtists };

  useEffect(() => {
    if (myPreds && id) {
      const p = myPreds[id];
      setLocal({
        headliners: p?.headliners || [],
        international: p?.international || [],
        danish: p?.danish || [],
        orange_opener: p?.orange_opener || null,
      });
    }
  }, [myPreds, id]);

  if (!meta || !festival) return <div className="p-8 text-zinc-400">Ukendt festival.</div>;

  const addArtist = (cat, artist) => {
    const key = CAT_META[cat].key;
    const arr = local[key];
    if (arr.includes(artist.artist_id)) { toast.error("Allerede valgt"); return; }
    if (arr.length >= festival.slots[cat]) { toast.error(`Max ${festival.slots[cat]} i ${CATEGORY_LABELS[cat]}`); return; }
    // Check same-festival duplicate across categories
    const allHere = [...local.headliners, ...local.international, ...local.danish];
    if (allHere.includes(artist.artist_id)) { toast.error("Kunstneren er allerede valgt i en anden kategori"); return; }
    setFreshArtists(m => ({ ...m, [artist.artist_id]: artist }));
    setLocal(s => ({ ...s, [key]: [...arr, artist.artist_id] }));
  };
  const removeArtist = (cat, artistId) => {
    const key = CAT_META[cat].key;
    setLocal(s => ({ ...s, [key]: s[key].filter(x => x !== artistId) }));
  };
  const setOrange = (artist) => {
    setFreshArtists(m => ({ ...m, [artist.artist_id]: artist }));
    setLocal(s => ({ ...s, orange_opener: artist.artist_id }));
  };
  const clearOrange = () => setLocal(s => ({ ...s, orange_opener: null }));

  const save = async () => {
    try {
      await api.put("/predictions", { festival_id: id, ...local });
      toast.success("Gemt");
      qc.invalidateQueries({ queryKey: ["my-predictions"] });
      qc.invalidateQueries({ queryKey: ["my-scores"] });
      qc.invalidateQueries({ queryKey: ["lb-overall"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme");
    }
  };

  const excludeAll = [...local.headliners, ...local.international, ...local.danish, ...(local.orange_opener ? [local.orange_opener] : [])];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <Link to="/forudsigelser" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6" data-testid="fest-back">
        <ArrowLeft size={14} /> Tilbage
      </Link>

      {/* Header */}
      <div className="relative rounded-3xl overflow-hidden mb-10 card-flat">
        <img src={resolveMediaUrl(festival.image_url) || meta.image} className="absolute inset-0 w-full h-full object-cover opacity-50" alt="" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent, rgba(0,0,0,0.85))` }} />
        <div className="relative p-8 sm:p-12">
          <div className="h-1 w-16 mb-4" style={{ background: meta.color }} />
          <div className="label-eyebrow">Festival Manager 2027</div>
          <h1 className="hero-title text-5xl sm:text-6xl lg:text-7xl mt-2">{festival.name}</h1>
          <div className="mt-4 text-zinc-300 text-sm flex flex-wrap gap-4">
            <span>{festival.slots.headliner} headlinere</span>
            <span>{festival.slots.international} internationale</span>
            <span>{festival.slots.danish} danske</span>
            {festival.has_orange_opener && <span className="text-orange-400">+ 1 Orange Scene åbner</span>}
          </div>
        </div>
      </div>

      {locked && (
        <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 p-4 flex items-center gap-3">
          <Lock size={16} className="text-red-400" />
          <div className="text-red-300 text-sm">Predictions er låst. Du kan ikke længere redigere.</div>
        </div>
      )}

      {/* Categories */}
      <div className="space-y-10">
        {CAT_ORDER.map(cat => {
          const arr = local[CAT_META[cat].key];
          const slot = festival.slots[cat];
          return (
            <section key={cat} data-testid={`section-${cat}`}>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-3">
                  <span>{CAT_META[cat].icon}</span> {CATEGORY_LABELS[cat]}
                  <span className="text-xs px-2 py-0.5 rounded-full border border-white/10 text-zinc-400">+{CAT_META[cat].points} pt</span>
                </h2>
                <div className="font-score text-2xl" data-testid={`count-${cat}`}>{arr.length}<span className="text-zinc-500">/{slot}</span></div>
              </div>
              {!locked && arr.length < slot && (
                <ArtistSearch
                  filter={CAT_META[cat].filter}
                  excludeIds={excludeAll}
                  onSelect={(a) => addArtist(cat, a)}
                  testId={`search-${cat}`}
                  placeholder={`Søg efter ${CATEGORY_LABELS[cat].toLowerCase()}…`}
                />
              )}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {arr.map(aid => {
                  const a = chipArtistMap[aid];
                  return (
                    <ArtistChip key={aid} artist={a} artistId={aid} onRemove={locked ? null : () => removeArtist(cat, aid)} testId={`chip-${cat}-${aid}`} removeTestId={`remove-${cat}-${aid}`} />
                  );
                })}
                {arr.length === 0 && <div className="text-zinc-600 text-sm col-span-full">Ingen valgt endnu.</div>}
              </div>
            </section>
          );
        })}

        {festival.has_orange_opener && (
          <section data-testid="section-orange-opener">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-3">
                <span style={{ color: "#FF4500" }}>🟠</span> Orange Scene åbner
                <span className="text-xs px-2 py-0.5 rounded-full border border-orange-500/30 text-orange-300">+10 bonus</span>
              </h2>
            </div>
            <p className="text-sm text-zinc-500 mb-3">Hvem tror du åbner Orange Scene?</p>
            {!locked && !local.orange_opener && (
              <ArtistSearch filter="any" excludeIds={[]} onSelect={setOrange} testId="search-orange" placeholder="Søg efter Orange Scene åbner…" />
            )}
            {local.orange_opener && (
              <ArtistChip artist={chipArtistMap[local.orange_opener]} artistId={local.orange_opener} onRemove={locked ? null : clearOrange} testId="chip-orange" removeTestId="remove-orange" subtitle="Orange Scene åbner (+10)" />
            )}
          </section>
        )}
      </div>

      {!locked && (
        <div className="sticky bottom-4 mt-12 z-30">
          <div className="glass rounded-2xl p-4 flex items-center justify-between">
            <div className="text-sm text-zinc-400">
              Valgt: <span className="text-white font-semibold">{excludeAll.length}</span>
            </div>
            <Button onClick={save} data-testid="save-prediction-btn" className="bg-white text-black hover:bg-zinc-200 rounded-full px-6">
              Gem forudsigelser
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
